import React from "react";
import { usePopper } from "react-popper";
import Portal from "components/Common/Portal";
import debounce from "lodash/debounce";

type Props = {
  children: React.ReactNode;
  renderContent?: () => React.ReactNode;
  disabled?: boolean;
};

export function Tooltip(props: Props) {
  const [referenceElement, setReferenceElement] = React.useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = React.useState<HTMLElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "fixed",
  });
  const [isHovered, setIsHovered] = React.useState(false);
  const [isTooltipDisplayed, setIsTooltipDisplayed] = React.useState(false);

  const displayAsDisabled = React.useMemo(() => {
    return props.disabled || typeof props.renderContent !== "function";
  }, [props.disabled, props.renderContent]);

  const onMouseEnter = () => {
    if (displayAsDisabled) {
      return;
    }
    setIsHovered(true);
  };
  const onMouseLeave = () => {
    setIsHovered(false);
  };

  const hideTooltipDebounced = debounce(() => {
    if (isHovered) {
      return;
    }
    setIsTooltipDisplayed(false);
  }, 100);

  React.useEffect(() => {
    if (isHovered) {
      setIsTooltipDisplayed(true);
    } else {
      hideTooltipDebounced();
    }
  }, [isHovered, hideTooltipDebounced]);

  React.useEffect(() => {
    if (!displayAsDisabled) {
      setIsTooltipDisplayed(false);
    }
  }, [displayAsDisabled]);

  return (
    <>
      <div
        tabIndex={0}
        ref={setReferenceElement}
        onPointerEnter={onMouseEnter}
        onPointerLeave={onMouseLeave}
        onFocus={onMouseEnter}
        onBlur={onMouseLeave}
        className="tailwind cursor-help"
      >
        {props.children}
      </div>

      <Portal>
        {isTooltipDisplayed && (
          <div className="tailwind" {...attributes.popper}>
            <div
              ref={setPopperElement}
              style={styles.popper}
              className="z-[1002]"
              onPointerEnter={onMouseEnter}
              onPointerLeave={onMouseLeave}
            >
              <div className="rounded-sm p-[1rem] bg-background-1 text-[1.4rem] leading-[1.6rem] max-w-[28rem]">
                {props.renderContent?.()}
              </div>
            </div>
          </div>
        )}
      </Portal>
    </>
  );
}
